/**
 * The role on a position row, editable in place — ROADMAP 9.10.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS IS WHAT MAKES "SKIP" SAFE TO OFFER
 *
 * The post-drop dialog lets a trader move on without answering, and the
 * strategy template seeds roles only for legs fired from a labelled strategy.
 * Everything else — a chain click, a skipped drop, a basket set up before
 * roles existed — reaches the book unlabelled, and without somewhere to say
 * so later those legs would stay unlabelled forever.
 *
 * WHY NOT A NATIVE `select`, WHICH IS WHAT THIS WAS
 *
 * It was chosen for being three options and no code, and it rendered as the
 * operating system's own dropdown in the middle of a dense trading table:
 * a system focus ring, a grey listbox, the placeholder shown as an option.
 * A control cannot be styled into this screen and also be the platform's
 * widget, and on the one panel a trader watches all day the platform's
 * widget is the wrong answer.
 *
 * So the chip is a button and the menu is ours. What the native element was
 * actually buying — keyboard access and not being clipped by the drawer's
 * scroll container — is paid for deliberately: arrow keys and Escape below,
 * and a portal to `document.body` so the menu is never cut off by an
 * ancestor's overflow.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check } from 'lucide-react'
import {
  isLegRole,
  LEG_ROLES,
  LEG_ROLE_LABEL,
  LEG_ROLE_MEANING,
  LEG_ROLE_SHORT,
  type LegRole,
} from '@apna/shared'
import { cn } from '@/lib/utils'
import { ROLE_TONE } from './legRoleTone'

const MENU_WIDTH = 168

export function LegRoleChip({
  role: given,
  onChange,
  disabled = false,
  testId,
  className,
}: {
  /** `undefined` is accepted and means the same as null — see below. */
  role: LegRole | null | undefined
  onChange: (role: LegRole | null) => void
  disabled?: boolean
  testId?: string
  /**
   * Let the caller mark this droppable in a narrow container.
   *
   * The positions drawer hides the chip before it will let the contract
   * name truncate — a role is an annotation set once and read rarely; the
   * contract is what the row IS.
   */
  className?: string
}) {
  /*
   * NORMALISED ONCE, HERE, AND NOWHERE ELSE IN THIS FILE.
   *
   * `undefined !== null` in JavaScript, so `role === null ? … : TONE[role]`
   * looks exhaustive and is not: an undefined falls to the second branch and
   * indexes the table with it, and `TONE[undefined].chip` throws while
   * rendering — taking the whole positions pane down with it.
   *
   * This exact slip has now cost this project four separate bugs. Treating
   * the prop as external input, once at the top, is the only version that
   * stays fixed.
   */
  const role: LegRole | null = isLegRole(given) ? given : null

  const [open, setOpen] = useState(false)
  const [at, setAt] = useState<{ top: number; left: number } | null>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const menu = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(0)

  /*
   * Measured AFTER layout and before paint, so the menu never appears at the
   * wrong place for a frame. Flipped upwards when it would run off the
   * bottom — this row can be near the foot of a drawer that is only 30% of
   * the viewport tall.
   */
  useLayoutEffect(() => {
    if (!open) return
    const r = trigger.current?.getBoundingClientRect()
    if (!r) return
    const height = menu.current?.offsetHeight ?? 150
    const below = r.bottom + 4
    const flip = below + height > window.innerHeight - 8
    setAt({
      top: flip ? Math.max(8, r.top - height - 4) : below,
      left: Math.min(Math.max(8, r.left), window.innerWidth - MENU_WIDTH - 8),
    })
    // Focus the menu itself, so the arrow keys below have somewhere to land.
    menu.current?.focus()
  }, [open])

  useEffect(() => {
    if (!open) return
    const close = () => setOpen(false)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setOpen(false)
        trigger.current?.focus()
      }
    }
    const onDown = (e: MouseEvent) => {
      if (menu.current?.contains(e.target as Node)) return
      if (trigger.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    // The drawer scrolls under a fixed menu, so the menu follows nothing —
    // closing is honest and cheaper than tracking the trigger.
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    window.addEventListener('keydown', onKey, true)
    document.addEventListener('mousedown', onDown)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
      window.removeEventListener('keydown', onKey, true)
      document.removeEventListener('mousedown', onDown)
    }
  }, [open])

  const choose = (next: LegRole | null) => {
    setOpen(false)
    if (next !== role) onChange(next)
    trigger.current?.focus()
  }

  const options: Array<LegRole | null> = role === null ? [...LEG_ROLES] : [...LEG_ROLES, null]

  return (
    <>
      <button
        ref={trigger}
        type="button"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={role ? `Role: ${LEG_ROLE_LABEL[role]}` : 'Set what this leg is for'}
        title={role ? LEG_ROLE_MEANING[role] : 'Say what this leg is for'}
        data-testid={testId}
        /*
         * The row is draggable. A press on this must open the menu, not pick
         * the leg up — and `draggable={false}` alone does not stop the
         * ancestor from claiming the gesture.
         */
        draggable={false}
        onDragStart={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={() => {
          setActive(role === null ? 0 : LEG_ROLES.indexOf(role))
          setOpen((o) => !o)
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setActive(role === null ? 0 : LEG_ROLES.indexOf(role))
            setOpen(true)
          }
        }}
        className={cn(
          className,
          'flex shrink-0 items-center gap-1 rounded-full px-1.5 py-[1px] text-[8px] font-bold uppercase tracking-wide',
          'transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary',
          'disabled:cursor-default disabled:opacity-40',
          role === null
            ? // Nearly invisible until the row is hovered: a placeholder on
              // every leg would be louder than the positions themselves.
              'border border-dashed border-muted-foreground/40 text-muted-foreground opacity-0 group-hover/leg:opacity-70 hover:!opacity-100'
            : cn(ROLE_TONE[role].chip, 'hover:brightness-95'),
        )}
      >
        {role !== null && <span className={cn('h-1 w-1 shrink-0 rounded-full', ROLE_TONE[role].dot)} />}
        {role === null ? 'ROLE' : LEG_ROLE_SHORT[role]}
      </button>

      {open &&
        createPortal(
          <div
            ref={menu}
            role="menu"
            aria-label="What this leg is for"
            data-testid={testId ? `${testId}-menu` : undefined}
            style={{ top: at?.top ?? -9999, left: at?.left ?? -9999, width: MENU_WIDTH }}
            className="fixed z-[60] overflow-hidden rounded-lg border border-border bg-card p-1 shadow-xl"
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault()
                setActive((i) => (i + 1) % options.length)
              } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setActive((i) => (i - 1 + options.length) % options.length)
              } else if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                choose(options[active] ?? null)
              }
            }}
            tabIndex={-1}
          >
            {options.map((option, i) => (
              <button
                key={option ?? 'clear'}
                type="button"
                role="menuitemradio"
                aria-checked={option === role}
                data-testid={`leg-role-option-${option ?? 'clear'}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(option)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[11.5px] transition-colors',
                  active === i ? 'bg-secondary' : 'bg-transparent',
                )}
              >
                {option === null ? (
                  <>
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full border border-muted-foreground/50" />
                    <span className="flex-1 text-muted-foreground">No role</span>
                  </>
                ) : (
                  <>
                    <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', ROLE_TONE[option].dot)} />
                    <span className="flex-1 font-medium">{LEG_ROLE_LABEL[option]}</span>
                  </>
                )}
                {option === role && <Check size={12} className="shrink-0 text-primary" />}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  )
}
